import type {
  GroupProDto,
  ProGroupListItemDto,
  UserProDto,
} from '@splitfiesta/shared';

import { formatProMovableAt, groupPro, proDialogVariant, userPro } from './pro';

export { formatNameList } from '../utils/formatNameList';

export interface NamedUser {
  userId: string;
  name: string;
}

export interface ReplaceCandidate {
  groupId: string;
  groupName: string;
  movableAt: string;
}

/** What the Pro dialog (web) and sheet (mobile) show. */
export type ProSheetVariant =
  | { kind: 'loading' }
  /** The Pro groups list failed; the caller's state cannot be told apart. */
  | { kind: 'error' }
  /** Not Pro: who made the group Pro and what that means, no call to action. */
  | { kind: 'member'; supporters: NamedUser[] }
  /** Pro caller; the add stays enabled when slots are full, the server answers. */
  | {
      kind: 'canAdd';
      slots: { used: number; total: number };
      alreadyBy: NamedUser[];
      /** An unspent first replace ignores the candidates' move dates. */
      firstMoveAvailable: boolean;
    }
  /** The caller holds this group's row, lapsed or not. */
  | { kind: 'holder'; movableAt: string };

export interface ProSheetModelInput {
  user: { id: string; pro?: UserProDto } | null | undefined;
  group: {
    id: string;
    pro?: GroupProDto;
    balances?: Array<{ userId: string; userName: string }>;
  };
  /** The caller's Pro groups from `GET /pro/groups`; undefined until loaded. */
  proGroups: readonly ProGroupListItemDto[] | undefined;
  listFailed: boolean;
  now?: string;
}

export interface ProSheetModel {
  variant: ProSheetVariant;
  replaceCandidates: ReplaceCandidate[];
  /** Whether the group is Pro right now, which decides the explainer copy. */
  groupIsPro: boolean;
}

export function proSheetModel({
  user,
  group,
  proGroups,
  listFailed,
  now,
}: ProSheetModelInput): ProSheetModel {
  const gp = groupPro(group, now);
  const replaceCandidates = (proGroups ?? [])
    .filter(item => item.groupId !== group.id)
    .map(({ groupId, groupName, movableAt }) => ({
      groupId,
      groupName,
      movableAt,
    }));

  const variant = ((): ProSheetVariant => {
    if (!user) return { kind: 'loading' };
    const caller = userPro(user, now);
    const names = new Map(
      (group.balances ?? []).map(member => [member.userId, member.userName])
    );
    // `by` is who made this group Pro, which is what "thanks to" names.
    const named = gp.by.flatMap(userId => {
      const name = names.get(userId);
      return name && userId !== user.id ? [{ userId, name }] : [];
    });

    if (!proGroups) {
      // The list only tells a lapsed holder apart from a plain member, so a
      // non-Pro viewer does not wait for it.
      if (!caller.active) return { kind: 'member', supporters: named };
      return listFailed ? { kind: 'error' } : { kind: 'loading' };
    }

    const kind = proDialogVariant({
      userPro: caller,
      groupPro: gp,
      userId: user.id,
      groupId: group.id,
      proGroups,
      now,
    });
    switch (kind) {
      case 'alreadyProByYou': {
        const held = proGroups.find(item => item.groupId === group.id);
        return {
          kind: 'holder',
          // Held only through a stale `by`: nothing to wait for on our side.
          movableAt: held?.movableAt ?? new Date(0).toISOString(),
        };
      }
      case 'notPro':
        return { kind: 'member', supporters: named };
      case 'add':
      case 'replaceFirstMove':
      case 'replaceCooldown':
      case 'slotsExhausted':
        return {
          kind: 'canAdd',
          slots: caller.slots,
          alreadyBy: named,
          firstMoveAvailable: caller.firstMoveAvailable,
        };
      default: {
        const unreachable: never = kind;
        return unreachable;
      }
    }
  })();

  return { variant, replaceCandidates, groupIsPro: gp.active };
}

const FORBIDDEN_KEYS = new Set([
  'errors:pro.notAvailable',
  'errors:pro.required',
]);

interface ProMutationError {
  status?: number;
  translationKey?: string;
  details?: unknown;
}

/** A refusal that closes the sheet: the caller cannot use Pro at all. */
export function isProForbiddenError(error: ProMutationError): boolean {
  const key = error.translationKey;
  return error.status === 403 || (!!key && FORBIDDEN_KEYS.has(key));
}

const PRO_KEYS = new Set([
  ...FORBIDDEN_KEYS,
  'errors:pro.slotsExhausted',
  'errors:pro.cooldown',
  'errors:pro.alreadyProByYou',
  'errors:pro.conflict',
  'errors:pro.groupNotFound',
]);

const DEFAULT_KEY = 'errors:api.status.default';

/**
 * The translated refusal for a Pro mutation, with the date or total from
 * `details`. `fallback` words anything that is not a Pro refusal, which each
 * platform reports its own way.
 */
export function proErrorMessage<E extends ProMutationError>(
  error: E,
  t: (key: string, options?: Record<string, unknown>) => string,
  language: string,
  fallback: (error: E) => string
): string {
  const key = error.translationKey;
  const details = (error.details ?? {}) as {
    movableAt?: unknown;
    total?: unknown;
  };

  if (!key || !PRO_KEYS.has(key)) return fallback(error);
  if (key === 'errors:pro.cooldown') {
    const date =
      typeof details.movableAt === 'string'
        ? formatProMovableAt(details.movableAt, language)
        : null;
    return date ? t(key, { date }) : t('errors:pro.conflict');
  }
  if (key === 'errors:pro.slotsExhausted') {
    return typeof details.total === 'number'
      ? t(key, { total: details.total, count: details.total })
      : t(DEFAULT_KEY);
  }
  return t(key);
}
